import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { CircleX } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { BrandLogo } from "@/components/atoms";
import { ROUTES } from "@/constants";
import { getBackofficeNavItems } from "@/constants";
import { useAuth, useTenant, useTenantBrand } from "@/hooks";
import { getTenantLandingUrl } from "@/utils";
import "./BackofficeSidebar.scss";

interface BackofficeSidebarProps {
  onClose: () => void;
}

/** Drawer sidebar for backoffice navigation. */
export const BackofficeSidebar: React.FC<BackofficeSidebarProps> = ({
  onClose,
}) => {
  const { logout, user } = useAuth();
  const { effectiveTenantSlug } = useTenant();
  const { getLogoSrc, logoAlt } = useTenantBrand();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleLogout = (): void => {
    logout();
    onClose();
    navigate(ROUTES.backoffice.login, { replace: true });
  };

  const handleLanding = (): void => {
    const landingUrl = getTenantLandingUrl(effectiveTenantSlug);
    onClose();

    if (landingUrl.startsWith(window.location.origin)) {
      navigate(ROUTES.public.home);
      return;
    }

    window.location.assign(landingUrl);
  };

  return (
    <Dialog.Content className="backoffice-sidebar">
      <Dialog.Title className="backoffice-sidebar__visually-hidden">
        Menú
      </Dialog.Title>
      <div className="backoffice-sidebar__header">
        <BrandLogo
          src={getLogoSrc("sidebar")}
          alt={logoAlt}
          className="backoffice-sidebar__logo-img"
        />
        <Dialog.Close asChild>
          <button
            type="button"
            className="backoffice-sidebar__close-btn"
            aria-label="Cerrar menú"
          >
            <CircleX />
          </button>
        </Dialog.Close>
      </div>

      {user && (
        <p className="backoffice-sidebar__user">
          {user.email}
          <br />({user.role})
        </p>
      )}

      <nav className="backoffice-sidebar__nav">
        {user &&
          getBackofficeNavItems(user.role, pathname).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                `backoffice-sidebar__nav-link${isActive ? " backoffice-sidebar__nav-link--active" : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
      </nav>

      <div className="backoffice-sidebar__footer">
        <button
          type="button"
          className="backoffice-sidebar__btn"
          onClick={handleLanding}
        >
          Ir a la landing
        </button>
        <button
          type="button"
          className="backoffice-sidebar__btn"
          onClick={handleLogout}
        >
          Cerrar sesión
        </button>
      </div>
    </Dialog.Content>
  );
};
